import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type {
  IHotelWithDetails,
  IMergeExperienceItem,
} from 'app/typings/migrationTool/HotelWithDetails';

import type { MergeSelection } from '../../HotelMigration';
import { MigrationLayout } from '../MigrationLayout';

type ExperiencesProps = {
  hotels?: IHotelWithDetails[];
  parentHotelId: number;
  childHotelId?: number;
  selections: Record<number, MergeSelection>;
  setSelections: (newSelections: Record<number, MergeSelection>) => void;
};

export const Experiences = ({
  hotels,
  parentHotelId,
  childHotelId,
  selections,
  setSelections,
}: ExperiencesProps) => {
  const { data: experienceCategories } = useExperiencesCategories();

  const parentHotel = hotels?.find((h) => h.hotel.id === parentHotelId);
  const childHotel = hotels?.find((h) => h.hotel.id === childHotelId);

  if (!parentHotel || !childHotel) {
    return null;
  }

  const actionList = parentHotel.experiences.map((experience) => {
    const experienceName = computeExperienceName({
      category: experienceCategories?.find(
        (cat) => cat.id === experience.categoryId
      ),
      quantity: experience.quantity ?? null,
      brand: experience.brand ?? null,
      duration: experience.duration ?? null,
      durationDescription: experience.durationDescription ?? null,
      time: experience.time ?? null,
      locations: experience.locations ?? null,
    });

    return {
      label: experienceName,
      isPublished: !!experience.isPublished,
      value: experience.id,
      disabled: Object.values(selections).some(
        (selection) => selection.targetId === experience.id
      ),
    };
  });

  return (
    <MigrationLayout<IMergeExperienceItem>
      title="Merge experiences from your child hotel with your parent hotel"
      description="You can decide to merge an existing experience, not migrate one, or create a new experience in the parent hotel."
      childHotel={childHotel.hotel.name}
      hotel={parentHotel}
      actionList={actionList}
      items={childHotel.experiences}
      getItemId={(experience) => experience.id}
      entityLabel="Experience"
      selections={selections}
      setSelections={setSelections}
    />
  );
};
